/**
 * BannerPreview — an image cropped exactly the way RN crops it.
 *
 * The RN brand-detail hero sits in a fixed-height container spanning the
 * content width and renders with `resizeMode="cover"` and a 12pt radius. This
 * mirrors that with `object-cover` at the same aspect ratio, so an admin
 * reviewing the pool sees the real crop rather than the whole photo — the
 * usual surprise is a portrait shot whose subject is entirely outside the
 * banner window.
 *
 * Size feedback is advisory, never blocking: rejecting a file would leave the
 * admin stuck, since there's no way to resize or re-crop inside this tool.
 */
import { useState, type ReactNode } from 'react';
import {
  BANNER_ASPECT_HEIGHT,
  BANNER_ASPECT_WIDTH,
  BANNER_MIN_RECOMMENDED_WIDTH,
} from '../../services/brandBannerFallbacks';

interface BannerPreviewProps {
  url: string;
  alt: string;
  /** Rendered in the corner of the crop, e.g. the enable/disable switch. */
  overlay?: ReactNode;
  /**
   * The slot's aspect ratio and recommended width. Default: the RN brand-detail
   * hero, which is what this preview was built for; other slots (the What's New
   * banner) pass their own.
   */
  aspect?: { width: number; height: number };
  minRecommendedWidth?: number;
}

interface Dimensions {
  width: number;
  height: number;
}

/**
 * Why the image is a poor banner, or `null` if it's fine.
 *
 * Portrait is checked first because it's the more damaging problem: a tall
 * image loses most of its subject to the crop regardless of resolution.
 */
export function sizeWarning(
  dimensions: Dimensions,
  ratio: number = BANNER_ASPECT_WIDTH / BANNER_ASPECT_HEIGHT,
  minRecommendedWidth: number = BANNER_MIN_RECOMMENDED_WIDTH,
): string | null {
  const { width, height } = dimensions;
  if (width <= 0 || height <= 0) return null;

  if (height > width) {
    return 'Portrait — most of this image is cropped away';
  }
  if (width / height < ratio * 0.75) {
    return 'Much taller than the banner slot — expect heavy cropping';
  }
  if (width < minRecommendedWidth) {
    return `Only ${width}px wide — may look soft (${minRecommendedWidth}px+ recommended)`;
  }
  return null;
}

export function BannerPreview({
  url,
  alt,
  overlay,
  aspect = { width: BANNER_ASPECT_WIDTH, height: BANNER_ASPECT_HEIGHT },
  minRecommendedWidth = BANNER_MIN_RECOMMENDED_WIDTH,
}: BannerPreviewProps) {
  const [dimensions, setDimensions] = useState<Dimensions | null>(null);
  const [failed, setFailed] = useState(false);

  const warning = dimensions
    ? sizeWarning(dimensions, aspect.width / aspect.height, minRecommendedWidth)
    : null;

  return (
    <div className="space-y-1.5">
      <div
        className="relative w-full overflow-hidden rounded-xl bg-gray-100"
        style={{ aspectRatio: `${aspect.width} / ${aspect.height}` }}
      >
        {failed ? (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
            Image failed to load
          </div>
        ) : (
          <img
            src={url}
            alt={alt}
            className="h-full w-full object-cover"
            onLoad={(e) =>
              setDimensions({
                width: e.currentTarget.naturalWidth,
                height: e.currentTarget.naturalHeight,
              })
            }
            onError={() => setFailed(true)}
          />
        )}
        {overlay ? <div className="absolute right-2 top-2">{overlay}</div> : null}
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-gray-500">
          {dimensions ? `${dimensions.width} × ${dimensions.height}` : '—'}
        </span>
        {warning ? (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
            {warning}
          </span>
        ) : null}
      </div>
    </div>
  );
}
